import { useCallback, useEffect, useRef, useState } from 'react';
import completionSound from '../assets/complete_sound (1).mp3';

export default function useTimerSound(timer, enabled) {
  const contextRef = useRef(null);
  const bufferRef = useRef(null);
  const loadingRef = useRef(null);
  const sourceRef = useRef(null);
  const scheduledAt = useRef(0);
  const handledCompletion = useRef(null);
  const mounted = useRef(true);
  const [ready, setReady] = useState(0);
  const [soundBlocked, setSoundBlocked] = useState(false);

  const stop = useCallback(() => {
    if (!sourceRef.current) return;
    try { sourceRef.current.stop(); } catch (_error) { /* Already ended. */ }
    sourceRef.current.disconnect();
    sourceRef.current = null;
  }, []);

  // Called directly from Start/Resume/Unmute so audio is unlocked by a gesture.
  const prepare = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = contextRef.current || new AudioContext();
      contextRef.current = context;
      context.onstatechange = () => {
        if (!mounted.current) return;
        setSoundBlocked(context.state !== 'running');
        if (context.state === 'running' && bufferRef.current) setReady((version) => version + 1);
      };
      const resume = context.resume();
      if (!loadingRef.current) {
        loadingRef.current = fetch(completionSound)
          .then((response) => {
            if (!response.ok) throw new Error('Sound unavailable');
            return response.arrayBuffer();
          })
          .then((buffer) => context.decodeAudioData(buffer))
          .then((buffer) => { bufferRef.current = buffer; });
      }
      Promise.all([resume, loadingRef.current]).then(() => {
        if (mounted.current) { setReady((version) => version + 1); setSoundBlocked(context.state !== 'running'); }
      }).catch(() => {
        loadingRef.current = null;
        if (mounted.current) setSoundBlocked(true);
      });
    } catch (_error) {
      setSoundBlocked(true);
    }
  }, []);

  useEffect(() => {
    const context = contextRef.current;
    if (!enabled || ['idle', 'paused'].includes(timer.status) || timer.acknowledged && timer.status === 'finished') {
      stop();
      return;
    }
    if (!ready || !bufferRef.current || context?.state !== 'running') return;
    if (timer.status === 'finished') {
      if (handledCompletion.current === timer.completedAt) return;
      handledCompletion.current = timer.completedAt;
      // A scheduled source may already be sounding while a hidden tab catches up.
      if (sourceRef.current && context.currentTime >= scheduledAt.current - 0.02) return;
      stop();
    } else {
      stop();
    }
    const source = context.createBufferSource();
    source.buffer = bufferRef.current;
    source.connect(context.destination);
    sourceRef.current = source;
    const delay = timer.status === 'running' ? Math.max(0, timer.endsAt - Date.now()) / 1000 : 0;
    // The audio engine schedules this independently of throttled UI callbacks.
    scheduledAt.current = context.currentTime + delay;
    source.start(scheduledAt.current);
  }, [enabled, ready, timer.status, timer.endsAt, timer.acknowledged, timer.completedAt, stop]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stop();
      contextRef.current?.close().catch(() => {});
    };
  }, [stop]);

  return { prepare, stop, soundBlocked: enabled && (soundBlocked || timer.status === 'running' && !ready) };
}
